/** @jsx jsx */
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  ChallengeGoalTypes,
  ChallengeTypeType,
  GoalType,
  ICustomTheme,
  ChallengeResultOrderType,
} from 'interfaces';
import { ChallengeRanking } from 'components';
import { ChallengeResultsDataType } from 'reducers/challenge.reducer';
import { GOAL } from 'constants/challenges';
import ChallengeRankCard from '../ChallengeRankCard';
import ChallengeSkeletonCard from '../ChallengeSkeletonCard';
import { useState } from 'react';

type Theme = {
  theme?: ICustomTheme;
};

type ChallengeRankListTypes = {
  areResultsAnonymized: boolean;
  challengeType: ChallengeTypeType;
  goals: ChallengeGoalTypes[];
  goalType: GoalType;
  isGroup: boolean;
  resultUnit: string | undefined;
};

const GoalUnit = styled('span')<Theme>(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  textTransform: 'lowercase',
  [theme.breakpoints.down('xs')]: {
    fontSize: theme.typography.caption.fontSize,
  },
}));

const ChallengeRankList = ({
  areResultsAnonymized,
  challengeType,
  goals,
  goalType,
  isGroup,
  resultUnit,
}: ChallengeRankListTypes): JSX.Element | null => {
  const { t } = useTranslation();
  const { id: challengeIdString }: { id: string } = useParams();
  const [order, setOrder] = useState<ChallengeResultOrderType>('asc');
  const challengeId = Number(challengeIdString);

  const handleItemProps =
    (goalType: GoalType) =>
    ({
      data,
      resultId,
    }: {
      data: ChallengeResultsDataType;
      resultId: number;
      index: number;
    }) => {
      const defaultProps = {
        areResultsAnonymized,
        mode: 'value',
        isGroup,
        entity: data.byId[resultId].entity,
        rank: data.byId[resultId].rank,
        resultValue: data.byId[resultId].resultValue,
      };

      switch (goalType) {
        case GOAL.RESULT:
          return {
            ...defaultProps,
            unit: resultUnit,
          };
        case GOAL.GENERIC: {
          const lastGoal = goals?.[goals.length - 1];
          return {
            ...defaultProps,
            goalValue: lastGoal?.value,
            unit: lastGoal?.unit,
          };
        }
        case GOAL.BY_PROFILE:
        case GOAL.CUSTOM:
          return {
            ...defaultProps,
            goalValue: data.byId[resultId].customResultValue,
            mode: 'percent',
            unit: (
              <GoalUnit>{t('challenges.detail.rank.of_the_goal')}</GoalUnit>
            ),
          };
        default:
          // eslint-disable-next-line no-console
          console.warn(
            `Wrong goal type in ChallengeRankList: ${goalType}.
          Should be one of ${GOAL.RESULT}, ${GOAL.GENERIC}, ${GOAL.CUSTOM} or ${GOAL.BY_PROFILE}.`,
          );
          return defaultProps;
      }
    };

  return (
    <ChallengeRanking
      title={t('challenges.detail.rank.title')}
      challengeId={challengeId}
      challengeType={challengeType}
      ItemSkeleton={() => <ChallengeSkeletonCard goalType={goalType} />}
      ItemComponent={ChallengeRankCard}
      getItemProps={handleItemProps(goalType)}
      order={order}
      setOrder={setOrder}
    />
  );
};

export default ChallengeRankList;
